/**
 * `completion.session_summary` — locally-aggregated completion telemetry.
 *
 * WHY THIS EXISTS (P0 of Docs/EditorPerfTelemetry-Plan.md): with
 * `quickSuggestions: true` the completion provider runs on virtually every
 * keystroke, so per-resolution `completion.resolved` events would fleet-scale
 * to 20-100x the Umami instance's entire current daily ingest. Resolutions
 * and dismissals therefore increment local counters and flush as ONE summary
 * event per engine on a bounded cadence. `completion.accepted` (explicit user
 * action) and `completion.orchestrator.error` stay per-event for detail but
 * are CAPPED per app run in `completionTelemetryWiring.ts` (2026-07-25 volume
 * audit) — this aggregate carries their exact totals (`acceptedCount`,
 * `error_<stage>`) so the capped per-event streams stay auditable and the
 * dashboard can re-source its rates to the aggregate.
 *
 * Latency histogram bucket edges [25, 50, 100, 250, 500, 750] are FROZEN —
 * the release-health budgets (p95 > 250ms amber, p99 > 750ms red) sit
 * exactly on edges, so bucket-based percentile verdicts are exact, and any
 * edge change breaks cross-release comparison.
 *
 * 1.5.1 keys (additive — the keys above keep their definitions):
 *   - `prov_0_5` / `prov_5_16` / `prov_16_50` / `prov_50_plus` — MEASURABLE
 *     provider runs by synchronous main-thread ms from the provider starting
 *     its work to returning the list (`providerCost.ts`: a run that awaited
 *     real I/O or crossed a task boundary is unmeasurable and lands in no
 *     bucket). Out-typed runs are included when measurable, which in practice
 *     they never are (the superseding keystroke ran mid-run), so the four sum
 *     to at most `resolvedCount + supersededCount` and ≈ resolved runs.
 *   - `truncatedCount` — resolves where the untyped cap removed candidates.
 *   - `items_1_100` / `items_101_300` / `items_301_500` / `items_501_plus` —
 *     resolves by returned item count (zero stays `zeroSuggestionCount`).
 * The interval cap counts only flushes that emitted something (F5), like
 * `feature:editor_input_latency`.
 *
 * Payload stays categorical/numeric: engine id + counts. Never SQL, never
 * identifiers, never error text.
 */

export const COMPLETION_SESSION_SUMMARY_EVENT = 'completion.session_summary';
export const SUMMARY_FLUSH_INTERVAL_MS = 10 * 60 * 1000;
/** Interval-flush cap per app run; the unload flush is always allowed. */
export const MAX_SUMMARY_INTERVAL_FLUSHES = 12;

/** FROZEN upper-exclusive histogram edges (ms). */
export const LATENCY_BUCKET_EDGES = [25, 50, 100, 250, 500, 750] as const;
const HISTOGRAM_KEYS = [
  'lat_0_25',
  'lat_25_50',
  'lat_50_100',
  'lat_100_250',
  'lat_250_500',
  'lat_500_750',
  'lat_750_plus',
] as const;

/** Upper-exclusive provider-cost edges (ms) — sit on the 16 ms frame and the 50 ms "slow keystroke" line. */
export const PROVIDER_COST_BUCKET_EDGES = [5, 16, 50] as const;
const PROVIDER_COST_KEYS = ['prov_0_5', 'prov_5_16', 'prov_16_50', 'prov_50_plus'] as const;
/** Inclusive upper edges of the returned-item-count buckets (the untyped cap is 500). */
const ITEM_COUNT_BUCKET_MAX = [100, 300, 500] as const;
const ITEM_COUNT_KEYS = ['items_1_100', 'items_101_300', 'items_301_500', 'items_501_plus'] as const;

export type ResolvedSample = {
  latencyMs: number;
  suggestionCount: number;
  /** 'slot' when the GRA-8 slot gate classified the position (which-path counter). */
  classifiedBy?: 'clause' | 'slot';
  valueSlotRole?: string;
  predicateHeadRole?: string;
  /** Synchronous main-thread provider ms; undefined when not measurable (awaited real I/O). */
  providerMs?: number;
  /** The untyped cap removed candidates from this list. */
  truncated?: boolean;
};

export type DismissReason = 'esc' | 'cursor-moved' | 'click-outside' | 'replaced' | 'other';

/**
 * Runtime allowlist for the dynamic `role_*` props — the union values of
 * ValueSlotRole + PredicateHeadRole (`sql-grammar/model/editorContext.ts`).
 * The aggregate accepts plain strings (deliberate decoupling), so without
 * this an unexpected caller value would mint an unbounded new prop name per
 * distinct string; unknown roles fold into `role_other` instead.
 */
const KNOWN_ROLES: ReadonlySet<string> = new Set([
  'comparison-rhs',
  'set-rhs',
  'between-low',
  'between-high',
  'in-list-item',
  'where-head',
  'having-head',
  'on-head',
]);

/** Orchestrator stages → `error_<stage>` props; unknown folds to `other`. */
const KNOWN_ERROR_STAGES: ReadonlySet<string> = new Set([
  'parse',
  'context',
  'resolve',
  'rank',
  'monaco-bridge',
]);

type EngineAggregate = {
  resolvedCount: number;
  histogram: number[];
  zeroSuggestionCount: number;
  classifiedBySlotCount: number;
  /** Closed-union role ids → counts, emitted as `role_<snake_cased>` props. */
  roleCounts: Map<string, number>;
  dismissed: Record<DismissReason, number>;
  supersededCount: number;
  /** Exact accept total — the per-event `completion.accepted` stream is capped. */
  acceptedCount: number;
  /** Exact per-stage error totals — the per-event error stream is capped. */
  errorCounts: Map<string, number>;
  providerCost: number[];
  truncatedCount: number;
  itemCounts: number[];
};

const aggregates = new Map<string, EngineAggregate>();
let flushTimer: ReturnType<typeof setInterval> | null = null;
let intervalFlushes = 0;
let unloadHookInstalled = false;

function aggregateFor(engineId: string): EngineAggregate {
  let agg = aggregates.get(engineId);
  if (!agg) {
    agg = {
      resolvedCount: 0,
      histogram: new Array(HISTOGRAM_KEYS.length).fill(0),
      zeroSuggestionCount: 0,
      classifiedBySlotCount: 0,
      roleCounts: new Map(),
      dismissed: { esc: 0, 'cursor-moved': 0, 'click-outside': 0, replaced: 0, other: 0 },
      supersededCount: 0,
      acceptedCount: 0,
      errorCounts: new Map(),
      providerCost: new Array(PROVIDER_COST_KEYS.length).fill(0),
      truncatedCount: 0,
      itemCounts: new Array(ITEM_COUNT_KEYS.length).fill(0),
    };
    aggregates.set(engineId, agg);
  }
  return agg;
}

export function latencyBucketIndex(latencyMs: number): number {
  for (let i = 0; i < LATENCY_BUCKET_EDGES.length; i++) {
    if (latencyMs < LATENCY_BUCKET_EDGES[i]) return i;
  }
  return LATENCY_BUCKET_EDGES.length;
}

export function providerCostBucketIndex(providerMs: number): number {
  for (let i = 0; i < PROVIDER_COST_BUCKET_EDGES.length; i++) {
    if (providerMs < PROVIDER_COST_BUCKET_EDGES[i]) return i;
  }
  return PROVIDER_COST_BUCKET_EDGES.length;
}

function itemCountBucketIndex(count: number): number {
  for (let i = 0; i < ITEM_COUNT_BUCKET_MAX.length; i++) {
    if (count <= ITEM_COUNT_BUCKET_MAX[i]) return i;
  }
  return ITEM_COUNT_BUCKET_MAX.length;
}

function recordProviderCost(agg: EngineAggregate, providerMs: number | undefined): void {
  if (providerMs === undefined || !Number.isFinite(providerMs)) return;
  agg.providerCost[providerCostBucketIndex(Math.max(0, providerMs))] += 1;
}

export function recordResolved(engineId: string, sample: ResolvedSample): void {
  const agg = aggregateFor(engineId);
  agg.resolvedCount += 1;
  agg.histogram[latencyBucketIndex(sample.latencyMs)] += 1;
  recordProviderCost(agg, sample.providerMs);
  if (sample.truncated) agg.truncatedCount += 1;
  if (sample.suggestionCount > 0) agg.itemCounts[itemCountBucketIndex(sample.suggestionCount)] += 1;
  if (sample.suggestionCount === 0) agg.zeroSuggestionCount += 1;
  if (sample.classifiedBy === 'slot') agg.classifiedBySlotCount += 1;
  for (const role of [sample.valueSlotRole, sample.predicateHeadRole]) {
    if (role) {
      const key = KNOWN_ROLES.has(role) ? role : 'other';
      agg.roleCounts.set(key, (agg.roleCounts.get(key) ?? 0) + 1);
    }
  }
  ensureFlushMachinery();
}

export function recordDismissed(engineId: string, reason: DismissReason): void {
  aggregateFor(engineId).dismissed[reason] += 1;
  ensureFlushMachinery();
}

/** A run started but was out-typed (cancelled/superseded) before resolving.
 *  `providerMs`: its main-thread cost when measurable (see `prov_*`). */
export function recordSuperseded(engineId: string, providerMs?: number): void {
  const agg = aggregateFor(engineId);
  agg.supersededCount += 1;
  recordProviderCost(agg, providerMs);
  ensureFlushMachinery();
}

/** Exact accept counter — pairs with the capped per-event `completion.accepted`. */
export function recordAccepted(engineId: string): void {
  aggregateFor(engineId).acceptedCount += 1;
  ensureFlushMachinery();
}

/** Exact per-stage error counter — pairs with the capped per-event error stream. */
export function recordOrchestratorErrorCount(engineId: string, stage: string): void {
  const agg = aggregateFor(engineId);
  const key = KNOWN_ERROR_STAGES.has(stage) ? stage : 'other';
  agg.errorCounts.set(key, (agg.errorCounts.get(key) ?? 0) + 1);
  ensureFlushMachinery();
}

function ensureFlushMachinery(): void {
  if (!flushTimer) {
    // Only EMITTING flushes count against the cap (F5): an idle editor's
    // empty intervals used to burn it, pushing later activity to the unload
    // flush — which a renderer crash or hang-reload loses.
    flushTimer = setInterval(() => {
      if (intervalFlushes >= MAX_SUMMARY_INTERVAL_FLUSHES) return;
      if (flushCompletionSessionSummary() > 0) intervalFlushes += 1;
    }, SUMMARY_FLUSH_INTERVAL_MS);
  }
  if (!unloadHookInstalled && typeof window !== 'undefined') {
    unloadHookInstalled = true;
    window.addEventListener('beforeunload', flushCompletionSessionSummary);
  }
}

/** Emit one summary per engine with any activity, then reset. Returns the
 *  number of events emitted (the interval cap counts only emitting flushes). */
export function flushCompletionSessionSummary(): number {
  let emitted = 0;
  for (const [engineId, agg] of aggregates) {
    const dismissedTotal = Object.values(agg.dismissed).reduce((a, b) => a + b, 0);
    const errorTotal = [...agg.errorCounts.values()].reduce((a, b) => a + b, 0);
    if (
      agg.resolvedCount === 0 &&
      dismissedTotal === 0 &&
      agg.supersededCount === 0 &&
      agg.acceptedCount === 0 &&
      errorTotal === 0
    ) continue;
    const payload: Record<string, string | number> = {
      engineId,
      resolvedCount: agg.resolvedCount,
      zeroSuggestionCount: agg.zeroSuggestionCount,
      classifiedBySlotCount: agg.classifiedBySlotCount,
      supersededCount: agg.supersededCount,
      acceptedCount: agg.acceptedCount,
      dismissedEsc: agg.dismissed.esc,
      dismissedCursorMoved: agg.dismissed['cursor-moved'],
      dismissedClickOutside: agg.dismissed['click-outside'],
      dismissedReplaced: agg.dismissed.replaced,
      dismissedOther: agg.dismissed.other,
    };
    HISTOGRAM_KEYS.forEach((key, i) => {
      payload[key] = agg.histogram[i];
    });
    PROVIDER_COST_KEYS.forEach((key, i) => {
      payload[key] = agg.providerCost[i];
    });
    payload.truncatedCount = agg.truncatedCount;
    ITEM_COUNT_KEYS.forEach((key, i) => {
      payload[key] = agg.itemCounts[i];
    });
    // Role ids come from closed unions (ValueSlotRole / PredicateHeadRole) —
    // low-cardinality by construction; snake_cased into prop names.
    for (const [role, count] of agg.roleCounts) {
      payload[`role_${role.replace(/-/g, '_')}`] = count;
    }
    // Per-stage error totals — closed set (KNOWN_ERROR_STAGES + 'other').
    for (const [stage, count] of agg.errorCounts) {
      payload[`error_${stage.replace(/-/g, '_')}`] = count;
    }
    emitted += 1;
    window.electronAPI?.analytics
      ?.track(COMPLETION_SESSION_SUMMARY_EVENT, payload)
      .catch(() => {});
  }
  aggregates.clear();
  return emitted;
}

/** Test-only: reset all module state. */
export function _resetCompletionSessionAggregateForTests(): void {
  aggregates.clear();
  intervalFlushes = 0;
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  if (unloadHookInstalled && typeof window !== 'undefined') {
    window.removeEventListener('beforeunload', flushCompletionSessionSummary);
    unloadHookInstalled = false;
  }
}
